<template>
    <div class="max-w-4xl mx-auto p-4 sm:p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
        <!-- Header -->
        <div class="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
            <div>
                <h2 class="text-2xl font-bold text-gray-800">Free Audio to Text</h2>
                <p class="text-gray-500 text-sm">Upload audio or video files to transcribe them into text instantly.</p>
            </div>
            <div class="flex bg-gray-100 p-1 rounded-xl">
                <button 
                    @click="mode = 'live'" 
                    :class="mode === 'live' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'"
                    class="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                >
                    Live Mic
                </button>
                <button 
                    @click="mode = 'upload'" 
                    :class="mode === 'upload' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'"
                    class="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                >
                    File Upload
                </button>
            </div>
        </div>

        <!-- Mode 1: Live Recording (Previously Added) -->
        <div v-if="mode === 'live'">
            <div class="relative group">
                <div 
                    class="min-h-[300px] max-h-[500px] overflow-y-auto p-6 bg-gray-50/50 rounded-xl border border-gray-200 focus-within:border-blue-300 transition-all relative"
                    :class="{ 'ring-2 ring-blue-100': recording }"
                >
                    <div v-if="!liveTranscript && !interimTranscript" class="absolute inset-0 flex flex-col items-center justify-center text-gray-400 pointer-events-none">
                        <Mic class="w-12 h-12 mb-3 opacity-20" />
                        <p class="text-sm">Click start and begin speaking...</p>
                    </div>
                    <div class="text-gray-800 leading-relaxed whitespace-pre-wrap">
                        <span>{{ liveTranscript }}</span>
                        <span v-if="interimTranscript" class="text-gray-400 italic">{{ interimTranscript }}</span>
                    </div>
                </div>

                <!-- Audio Preview & Actions (Live Mode) -->
                <div v-if="liveAudioUrl" class="mt-8 bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                    <div class="p-4 border-b border-gray-100 bg-white flex items-center justify-between">
                        <div class="flex items-center gap-2">
                             <PlayCircle class="w-5 h-5 text-blue-600" />
                             <span class="text-xs font-bold text-gray-700 uppercase tracking-widest leading-none">Audio Player</span>
                        </div>
                    </div>
                    
                    <div class="p-6">
                        <audio :src="liveAudioUrl" controls class="w-full h-10 mb-6"></audio>

                        <div class="grid grid-cols-3 gap-4">
                            <button 
                                @click="downloadAudio" 
                                class="flex flex-1 items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm transition-all text-blue-600 font-bold text-sm"
                            >
                                <Download class="w-4 h-4" />
                                Download Audio
                            </button>
                            <button 
                                v-if="liveTranscript" 
                                @click="copyToClipboard" 
                                class="flex flex-1 items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm transition-all text-sm font-bold"
                                :class="copied ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-600'"
                            >
                                <Check v-if="copied" class="w-4 h-4" />
                                <Copy v-else class="w-4 h-4" />
                                {{ copied ? 'Copied!' : 'Copy Result' }}
                            </button>
                            <!-- Try Again / Reset button -->
                            <button @click="resetTranscription" class="flex flex-1 items-center justify-center gap-2 px-6 py-3 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 text-red-600 font-bold text-sm shadow-sm transition-all transform active:scale-95">
                                <RefreshCw class="w-4 h-4" />
                                Try New
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-8 flex flex-col items-center justify-center gap-4">
                <div v-if="recording" class="flex gap-4">
                    <!-- Pause/Resume Button -->
                     <button 
                        @click="togglePause"
                        class="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95"
                    >
                        <Pause v-if="!isPaused" class="w-5 h-5" />
                        <Play v-else class="w-5 h-5 fill-current" />
                        {{ isPaused ? 'Resume' : 'Pause' }}
                    </button>

                    <!-- Stop Button -->
                    <button 
                        @click="stopRecording"
                        class="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95"
                    >
                        <Square class="w-5 h-5 fill-current" />
                        Finish
                    </button>
                </div>

                <button 
                    v-else-if="!liveAudioUrl"
                    @click="startRecording"
                    class="bg-blue-600 hover:bg-blue-700 ring-4 ring-blue-50 flex items-center gap-3 text-white px-10 py-4 rounded-xl font-semibold text-lg shadow-xl transform transition-all active:scale-95 hover:-translate-y-0.5"
                >
                    <Mic class="w-6 h-6" />
                    Start Speaking
                </button>
                
                <div v-if="recording" class="flex flex-col items-center gap-1">
                    <p class="text-red-500 text-xs font-bold animate-pulse uppercase tracking-widest italic">
                        {{ isPaused ? 'Recording Paused' : 'Recording in progress...' }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Mode 2: File Upload (Whisper API) -->
        <div v-else>
            <!-- Upload Zone -->
            <div 
                v-if="!selectedFile"
                @click="$refs.fileInput.click()"
                @dragover.prevent="dragActive = true"
                @dragleave.prevent="dragActive = false"
                @drop.prevent="handleDrop"
                :class="dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50/50'"
                class="border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer hover:border-blue-400 transition-all group"
            >
                <input type="file" ref="fileInput" class="hidden" accept="audio/*,video/*" @change="handleFileSelect">
                <div class="flex flex-col items-center">
                    <div class="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Upload class="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 class="text-lg font-bold text-gray-800">Upload Media File</h3>
                    <p class="text-gray-500 text-xs mt-1">Supports MP3, WAV, M4A, FLAC, AAC, MP4, MOV, AVI, MKV (Max 25MB)</p>
                    <button class="mt-6 bg-white border border-gray-200 px-6 py-2 rounded-lg text-sm font-semibold shadow-sm hover:bg-gray-50">Browse Files</button>
                </div>
            </div>

            <!-- File Selected Status -->
            <div v-else class="space-y-6">
                <div class="p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="p-2 bg-white rounded-lg"><Music class="w-5 h-5 text-blue-600" /></div>
                        <div>
                            <p class="text-sm font-bold text-gray-800 truncate max-w-[200px]">{{ selectedFile.name }}</p>
                            <p class="text-xs text-gray-500">{{ (selectedFile.size / (1024 * 1024)).toFixed(2) }} MB</p>
                        </div>
                    </div>
                    <button @click="resetTranscription" class="text-gray-400 hover:text-red-500"><X class="w-5 h-5" /></button>
                </div>


                <!-- Premium Skeleton Loader (YouTube Style) -->
                <div v-if="isProcessing" class="space-y-4 animate-pulse">
                    <div class="flex items-center gap-2 mb-2">
                        <div class="h-4 w-24 bg-gray-200 rounded-full"></div>
                    </div>
                    <div class="space-y-3 p-6 bg-gray-50 rounded-xl border border-gray-100">
                        <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div class="h-4 bg-gray-200 rounded w-full"></div>
                        <div class="h-4 bg-gray-200 rounded w-5/6"></div>
                        <div class="h-4 bg-gray-200 rounded w-4/6"></div>
                    </div>
                    <div class="flex justify-center flex-col items-center gap-2">
                        <p class="text-sm font-medium text-blue-600">AI is transcribing...</p>
                        <div class="w-48 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div class="h-full bg-blue-600 rounded-full w-1/2 animate-[shimmer_2s_infinite]"></div>
                        </div>
                    </div>
                </div>

                <div v-else-if="uploadTranscript" class="space-y-4">
                    <div class="p-6 bg-gray-50 rounded-xl border border-gray-200 relative group min-h-[100px]">
                        <div v-if="uploadDetectedLanguage" class="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold uppercase tracking-widest leading-none">
                            <Check class="w-3 h-3" />
                            Detected: {{ uploadDetectedLanguage }}
                        </div>
                        <p class="text-gray-800 leading-relaxed break-words">{{ uploadTranscript }}</p>
                    </div>
                </div>

                <div v-else class="pt-4">
                    <button 
                        @click="transcribeFile" 
                        class="w-full sm:w-auto mx-auto bg-blue-600 text-white px-10 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all active:scale-95 flex items-center justify-center gap-3"
                    >
                        <Zap class="w-5 h-5 fill-current" />
                        Transcribe Now
                    </button>
                </div>
                
                <!-- Media Player for Uploaded File (Only show after transcription) -->
                <div v-if="uploadAudioUrl && uploadTranscript" class="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                    <div class="p-4 border-b border-gray-100 bg-white flex items-center justify-between">
                        <div class="flex items-center gap-2">
                             <PlayCircle class="w-5 h-5 text-blue-600" />
                             <span class="text-xs font-bold text-gray-700 uppercase tracking-widest leading-none">Audio Preview</span>
                        </div>
                    </div>
                    
                    <div class="p-6">
                        <audio :src="uploadAudioUrl" controls class="w-full h-10 mb-6"></audio>
                        
                        <!-- Actions Row -->
                        <div class="flex flex-col sm:flex-row gap-3">
                            <button @click="downloadAudio" class="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 text-blue-600 font-bold text-sm shadow-sm transition-all transform active:scale-95">
                                <Download class="w-4 h-4" />
                                Download Audio
                            </button>

                            <button 
                                v-if="uploadTranscript"
                                @click="copyToClipboard" 
                                class="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-bold text-sm shadow-sm transition-all transform active:scale-95"
                                :class="copied ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-600'"
                            >
                                <Check v-if="copied" class="w-4 h-4" />
                                <Copy v-else class="w-4 h-4" />
                                {{ copied ? 'Copied!' : 'Copy Result' }}
                            </button>
                            
                            <!-- Try Again / Reset button - More prominent -->
                            <button @click="resetTranscription" class="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 text-red-600 font-bold text-sm shadow-sm transition-all transform active:scale-95">
                                <RefreshCw class="w-4 h-4" />
                                Try New
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- Global Error -->
        <div v-if="error" class="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
            <AlertCircle class="w-5 h-5 text-red-600 mt-0.5" />
            <div class="flex-1">
                <p class="text-sm font-semibold text-red-800">Error</p>
                <p class="text-xs text-red-600">{{ error }}</p>
            </div>
            <button @click="error = null" class="text-red-400"><X class="w-4 h-4" /></button>
        </div>

    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { 
    Mic, Square, Copy, Upload, Music, X, 
    AlertCircle, Zap, Check, PlayCircle, Download, Pause, Play, Volume2, RefreshCw
} from 'lucide-vue-next';

export default {
    name: 'AudioToTextComponent',
    components: {
        Mic, Square, Copy, Upload, Music, X, AlertCircle, Zap, Check, PlayCircle, Download, Pause, Play, Volume2, RefreshCw
    },
    setup() {
        const mode = ref('upload'); 
        
        // --- Live Mic State ---
        const liveTranscript = ref('');
        const liveAudioUrl = ref(null);
        const liveDetectedLanguage = ref('');
        const liveError = ref(null);
        
        // --- Upload State ---
        const uploadTranscript = ref('');
        const uploadAudioUrl = ref(null);
        const uploadDetectedLanguage = ref('');
        const uploadError = ref(null);
        const selectedFile = ref(null);

        // Shared states
        const dragActive = ref(false);
        const isProcessing = ref(false);
        const interimTranscript = ref('');
        const recording = ref(false);
        const isPaused = ref(false);
        const copied = ref(false);

        // Computed error for the global display
        const error = ref(null);

        let recognition = null;
        let mediaRecorder = null;
        let audioChunks = [];

        // --- Live Recording Logic ---
        const setupRecognition = () => {
            if (!('webkitSpeechRecognition' in window)) return;
            const SpeechRecognition = window.webkitSpeechRecognition;
            recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.onresult = (e) => {
                let interim = '';
                for (let i = e.resultIndex; i < e.results.length; ++i) {
                    if (e.results[i].isFinal) liveTranscript.value += e.results[i][0].transcript + ' ';
                    else interim += e.results[i][0].transcript;
                }
                interimTranscript.value = interim;
            };
        };

        const startRecording = async () => {
            if (!recognition) setupRecognition();
            
            recording.value = true; 
            isPaused.value = false;
            liveTranscript.value = ''; 
            liveDetectedLanguage.value = '';
            if (liveAudioUrl.value) URL.revokeObjectURL(liveAudioUrl.value);
            liveAudioUrl.value = null;
            error.value = null;
            
            if (recognition) recognition.start();

            // Start Media Recording for playback
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaRecorder = new MediaRecorder(stream);
                audioChunks = [];
                mediaRecorder.ondataavailable = (e) => audioChunks.push(e.data);
                mediaRecorder.onstop = () => {
                    const blob = new Blob(audioChunks, { type: 'audio/wav' });
                    liveAudioUrl.value = URL.createObjectURL(blob);
                };
                mediaRecorder.start();
            } catch (e) {
                error.value = "Microphone access denied.";
                recording.value = false;
                if (recognition) recognition.stop();
            }
        };

        const togglePause = () => {
            if (!mediaRecorder) return;
            if (isPaused.value) {
                mediaRecorder.resume();
                if (recognition) recognition.start();
                isPaused.value = false;
            } else {
                mediaRecorder.pause();
                if (recognition) recognition.stop();
                isPaused.value = true;
            }
        };

        const stopRecording = () => {
            recording.value = false; 
            isPaused.value = false;
            if (recognition) recognition.stop();
            if (mediaRecorder) mediaRecorder.stop();
        };

        // --- File Upload Logic (Whisper) ---
        const handleFileSelect = (e) => {
            const file = e.target.files[0];
            if (file) validateAndSetFile(file);
        };

        const handleDrop = (e) => {
            dragActive.value = false;
            const file = e.dataTransfer.files[0];
            if (file) validateAndSetFile(file);
        };

        const validateAndSetFile = (file) => {
            const allowedExtensions = ['mp3', 'wav', 'm4a', 'ogg', 'oga', 'webm', 'flac', 'aac', 'mp4', 'mov', 'avi', 'mkv'];
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (!file.type.startsWith('audio/') && !file.type.startsWith('video/') && !allowedExtensions.includes(fileExtension)) {
                error.value = "Please upload a valid audio or video file.";
                return;
            }
            if (file.size > 25 * 1024 * 1024) {
                error.value = "File size exceeds 25MB limit.";
                return;
            }
            if (uploadAudioUrl.value) URL.revokeObjectURL(uploadAudioUrl.value);
            selectedFile.value = file;
            uploadAudioUrl.value = URL.createObjectURL(file);
            uploadTranscript.value = '';
            uploadDetectedLanguage.value = '';
            error.value = null;
        };

        const transcribeFile = async () => {
            if (!selectedFile.value) return;
            
            isProcessing.value = true;
            error.value = null;

            const formData = new FormData();
            formData.append('audio', selectedFile.value);

            try {
                const response = await axios.post('/transcribe-audio', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                uploadTranscript.value = response.data.text;
                uploadDetectedLanguage.value = response.data.language;
            } catch (err) {
                error.value = err.response?.data?.error || "Failed to transcribe audio. Please try again.";
            } finally {
                isProcessing.value = false;
            }
        };
        
        const resetTranscription = () => {
            if (mode.value === 'live') {
                if (liveAudioUrl.value) URL.revokeObjectURL(liveAudioUrl.value);
                liveAudioUrl.value = null;
                liveTranscript.value = '';
                liveDetectedLanguage.value = '';
            } else {
                if (uploadAudioUrl.value) URL.revokeObjectURL(uploadAudioUrl.value);
                selectedFile.value = null;
                uploadAudioUrl.value = null;
                uploadTranscript.value = '';
                uploadDetectedLanguage.value = '';
            }
            error.value = null;
        };

        const copyToClipboard = async () => {
            const textToCopy = mode.value === 'live' ? liveTranscript.value : uploadTranscript.value;
            await navigator.clipboard.writeText(textToCopy);
            copied.value = true;
            setTimeout(() => copied.value = false, 2000);
        };

        onMounted(() => setupRecognition());

        const downloadAudio = () => {
            const url = mode.value === 'live' ? liveAudioUrl.value : uploadAudioUrl.value;
            if (!url) return;
            const link = document.createElement('a');
            link.href = url;
            link.download = `recording_${Date.now()}.wav`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return {
            mode, selectedFile, liveAudioUrl, uploadAudioUrl, dragActive, isProcessing, 
            liveTranscript, uploadTranscript, interimTranscript, liveDetectedLanguage, uploadDetectedLanguage, recording, isPaused, error, copied,
            handleFileSelect, handleDrop, transcribeFile, resetTranscription,
            startRecording, stopRecording, togglePause, downloadAudio, copyToClipboard
        };
    }
};
</script>

<style scoped>
@keyframes shimmer {
    0% { transform: translateX(-150%); }
    100% { transform: translateX(150%); }
}

.animate-shimmer {
    animation: shimmer 2s infinite linear;
}
</style>
